import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const GoogleButton = ({ label }) => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const { t } = useLang();
  const redirectUrl = window.location.origin + "/auth/google";
  return (
    <a
      data-testid="google-login-button"
      href={`https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`}
      className="w-full flex items-center justify-center gap-3 border border-cream/20 text-cream font-mono text-[11px] tracking-[0.25em] uppercase py-4 hover:border-gold hover:text-gold transition-colors"
    >
      <svg width="15" height="15" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10c5.35 0 9.25-3.67 9.25-9.09c0-1.15-.15-1.81-.15-1.81"/></svg>
      {label}
    </a>
  );
};

export const Inloggen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const next = location.state?.next || "/account";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(location.state?.next || next, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
      <div className="w-full max-w-md">
        <PageHeader eyebrow={t("auth.welcome")} title={<>{t("nav.login").charAt(0)}<span className="italic normal-case text-gold">{t("nav.login").slice(1)}</span></>} />
        <form data-testid="login-form" onSubmit={submit} className="border border-gold/15 bg-ink p-8 space-y-5">
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("auth.email")}</span>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-obsidian border border-gold/15 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-gold/50" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke block mb-2">{t("auth.password")}</span>
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-obsidian border border-gold/15 px-4 py-3.5 text-sm text-cream focus:outline-none focus:border-gold/50" />
          </label>
          {error && <p data-testid="login-error" className="text-xs text-red-400">{error}</p>}
          <button type="submit" data-testid="login-submit-button" disabled={busy} className="w-full bg-gold text-obsidian font-mono text-[11px] tracking-[0.3em] uppercase py-4 hover:bg-gold-light transition-colors disabled:opacity-40">
            {busy ? t("auth.busy") : t("auth.loginBtn")}
          </button>
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-gold/10" /><span className="font-mono text-[9px] text-smoke uppercase tracking-[0.2em]">{t("auth.or")}</span><span className="h-px flex-1 bg-gold/10" />
          </div>
          <GoogleButton label={t("auth.google")} />
          <div className="flex justify-between pt-2">
            <Link data-testid="to-forgot-password" to="/wachtwoord-vergeten" className="font-mono text-[10px] tracking-[0.15em] uppercase text-mist hover:text-gold transition-colors">{t("auth.forgot")}</Link>
            <Link data-testid="to-register" to="/registreren" className="font-mono text-[10px] tracking-[0.15em] uppercase text-gold hover:text-gold-light transition-colors">{t("auth.regLink")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Inloggen;
